import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../lib/auth.jsx";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { getOrCreateDeviceId, setDeviceLabel } from "../lib/device";
import { Smartphone, Monitor, Trash2, ShieldCheck, Loader2, AlertTriangle, Download, FileText } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const [devices, setDevices] = useState([]);
  const [maxDevices, setMax] = useState(4);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [attesting, setAttesting] = useState(false);
  const currentDeviceId = getOrCreateDeviceId();
  const [hpcsaInput, setHpcsaInput] = useState("");
  const [hpcsaRole, setHpcsaRole] = useState(user?.role_type === "student" ? "student" : "doctor");
  const [savingHpcsa, setSavingHpcsa] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [downloadingRef, setDownloadingRef] = useState(null);
  const isGoogleUser = !!user?.google_linked;

  const loadInvoices = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/payments");
      setInvoices(data.payments || []);
    } catch (e) {
      console.error("Failed to load invoices:", e);
    }
  }, []);

  const downloadInvoice = async (reference) => {
    setDownloadingRef(reference);
    try {
      const res = await api.get(`/subscription/invoice/${encodeURIComponent(reference)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RapidCareSA-invoice-${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download invoice. Please try again.");
    } finally {
      setDownloadingRef(null);
    }
  };

  const loadDevices = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/devices");
      setDevices(data.devices || []);
      setMax(data.max_devices || 4);
    } catch (e) {
      console.error("Failed to load devices:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const revoke = async (id) => {
    if (id === currentDeviceId) {
      if (!confirm("This is the device you are signed in on. Revoking it will sign you out immediately. Continue?")) return;
    }
    setRevoking(id);
    try {
      await api.delete(`/auth/devices/${encodeURIComponent(id)}`);
      toast.success("Device removed");
      if (id === currentDeviceId) {
        localStorage.removeItem("rcsa_token");
        localStorage.removeItem("rcsa_user");
        window.location.href = "/login";
        return;
      }
      await loadDevices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove device");
    } finally { setRevoking(null); }
  };

  const reAttest = async () => {
    setAttesting(true);
    try {
      await api.post("/auth/attest-hpcsa");
      toast.success("Registration confirmed");
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm");
    } finally { setAttesting(false); }
  };

  const renameThisDevice = () => {    const next = prompt("Label this device (visible to you only):");
    if (!next) return;
    setDeviceLabel(next);
    toast.success("Saved — refreshing device list");
    setTimeout(loadDevices, 400);
  };

  const saveHpcsa = async (e) => {    e.preventDefault();
    const v = hpcsaInput.trim().toUpperCase();
    if (v.length < 3) { toast.error("Enter your full HPCSA / MP / student registration number."); return; }
    setSavingHpcsa(true);
    try {
      await api.post("/auth/complete-hpcsa", { hpcsa_number: v, role_type: hpcsaRole });
      toast.success("Registration number saved — thank you.");
      await refresh?.();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save your number. Please try again.");
    } finally { setSavingHpcsa(false); }
  };

  const deleteAccount = async () => {
    if (delConfirm.trim() !== "DELETE") {
      toast.error('Please type DELETE to confirm.');
      return;
    }
    if (!isGoogleUser && !delPassword) {
      toast.error("Please enter your current password.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/account", {
        data: { confirmation: delConfirm.trim(), password: delPassword || undefined },
      });
      toast.success("Your account has been permanently deleted.");
      localStorage.removeItem("rcsa_token");
      localStorage.removeItem("rcsa_user");
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete your account. Please try again.");
      setDeleting(false);
    }
  };

  if (!user) return null;

  const isPaid = user.subscription_status === "active" || user.subscription_status === "trial";
  const planLabels = { "plan-monthly": "Premium Monthly", "plan-annual": "Premium Annual", "plan-free": "Free Access" };
  const planLabel = planLabels[user.subscription_plan] || user.subscription_plan || "Free";
  let daysLeft = null;
  if (user.subscription_expires_at) {
    daysLeft = Math.ceil((new Date(user.subscription_expires_at) - new Date()) / 86400000);
  }
  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Account</div>
        <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1">Settings</h1>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-4">
          <h3 className="font-heading font-semibold text-[#111827]">Profile</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div><div className="text-slate-500">Full name</div><div className="font-medium">{user.full_name}</div></div>
            <div><div className="text-slate-500">Email</div><div className="font-medium">{user.email}</div></div>
            <div><div className="text-slate-500">Role</div><div className="font-medium capitalize">{user.role}</div></div>
            <div><div className="text-slate-500">HPCSA / Practice #</div><div className="font-medium">{user.hpcsa_number || "—"}</div></div>
          </div>
        </CardContent>
      </Card>

      {!user.hpcsa_number && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="add-hpcsa-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-700" />
              <h3 className="font-heading font-semibold text-[#111827]">Add your registration number</h3>
            </div>
            <p className="text-sm text-slate-600">
              RapidCare SA is for HPCSA-verified clinicians and medical students only. Add your registration number to unlock your free trial and premium upgrades.
            </p>
            <form onSubmit={saveHpcsa} className="space-y-3" data-testid="add-hpcsa-form">
              <div className="grid grid-cols-2 gap-2 max-w-sm">
                <button
                  type="button"
                  onClick={() => setHpcsaRole("doctor")}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium text-left transition ${hpcsaRole === "doctor" ? "border-[#0B5FFF] bg-[#F1F5F9] text-[#111827]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                  data-testid="add-hpcsa-role-doctor"
                >
                  Qualified clinician
                </button>
                <button
                  type="button"
                  onClick={() => setHpcsaRole("student")}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium text-left transition ${hpcsaRole === "student" ? "border-[#0B5FFF] bg-[#F1F5F9] text-[#111827]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                  data-testid="add-hpcsa-role-student"
                >
                  Medical student
                </button>
              </div>
              <input
                value={hpcsaInput}
                onChange={(e) => setHpcsaInput(e.target.value)}
                placeholder={hpcsaRole === "student" ? "e.g. STU-2024-12345" : "e.g. MP0123456"}
                autoComplete="off"
                required
                data-testid="add-hpcsa-input"
                className="w-full max-w-sm rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]"
              />
              <Button type="submit" disabled={savingHpcsa} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid="add-hpcsa-submit">
                {savingHpcsa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save registration number
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-[#111827]">Subscription</h3>
            <Badge className={
              user.subscription_status === "active" ? "bg-emerald-100 text-emerald-800 border-0" :
              user.subscription_status === "trial" ? "bg-amber-100 text-amber-800 border-0" :
              "bg-slate-100 text-slate-700 border-0"
            }>{user.subscription_status}</Badge>
          </div>
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">Plan: </span><span className="font-medium">{planLabel}</span></div>
            <div><span className="text-slate-500">{isPaid ? "Access until: " : "Expires: "}</span><span className="font-medium">
              {user.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : "—"}
            </span>
            {daysLeft != null && daysLeft >= 0 && <span className="text-slate-500"> ({daysLeft} day{daysLeft === 1 ? "" : "s"} left)</span>}
            </div>
          </div>

          {isPaid && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="subscription-billing-note">
              Your premium access is a <span className="font-medium text-slate-700">fixed once-off period</span> — there is <span className="font-medium text-slate-700">no automatic billing</span> and nothing to cancel. Access simply ends on the date above. Renew anytime to continue without interruption.
            </div>
          )}

          {invoices.length > 0 && (
            <div className="pt-1" data-testid="invoices-section">
              <div className="text-sm font-medium text-[#111827] mb-2">Invoices &amp; receipts</div>
              <div className="space-y-2">
                {invoices.map((inv) => (
                  <div key={inv.reference} className="flex items-center justify-between p-3 border border-slate-200 rounded-md bg-white" data-testid={`invoice-row-${inv.reference}`}>
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <div className="font-medium text-sm text-[#111827]">{inv.plan_label} — R{Number(inv.amount_zar).toFixed(2)}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{inv.date} · {inv.period_label} · Invoice {inv.invoice_number}</div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadInvoice(inv.reference)}
                      disabled={downloadingRef === inv.reference}
                      data-testid={`invoice-download-${inv.reference}`}
                    >
                      {downloadingRef === inv.reference
                        ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Preparing…</>
                        : <><Download className="w-3.5 h-3.5 mr-1" /> Download</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Link to="/pricing"><Button className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid="manage-subscription">
            {isPaid ? "Renew or change plan" : "Upgrade to Premium"}
          </Button></Link>
        </CardContent>
      </Card>

      {/* Trusted devices (Option A) */}
      <Card className="border-slate-200" data-testid="trusted-devices-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-[#111827]">Trusted devices</h3>
            <Badge className="bg-slate-100 text-slate-700 border-0">{devices.length} / {maxDevices}</Badge>
          </div>
          <p className="text-xs text-slate-500">
            Your account may be used on a maximum of <strong>{maxDevices} devices</strong> at any time to prevent sharing. Signing in beyond this limit will prompt you to remove one of the devices below.
          </p>
          {loading ? (
            <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading...</div>
          ) : devices.length === 0 ? (
            <div className="text-sm text-slate-500">No devices on file yet.</div>
          ) : (
            <div className="space-y-2">
              {devices.map((d) => (
                <div key={d.device_id} className="flex items-start justify-between p-3 border border-slate-200 rounded-md bg-white" data-testid={`settings-device-${d.device_id}`}>
                  <div className="flex items-start gap-3">
                    {(d.label || "").toLowerCase().match(/phone|android|ios|iphone|ipad/) ? (
                      <Smartphone className="w-5 h-5 text-slate-400 mt-0.5" />
                    ) : (
                      <Monitor className="w-5 h-5 text-slate-400 mt-0.5" />
                    )}
                    <div>
                      <div className="font-medium text-sm text-[#111827]">
                        {d.label || "Unknown device"}
                        {d.device_id === currentDeviceId && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">This device</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Added {fmt(d.first_seen)} · last used {fmt(d.last_seen)}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled={revoking === d.device_id} onClick={() => revoke(d.device_id)}
                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid={`settings-device-revoke-${d.device_id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> {revoking === d.device_id ? "Removing..." : "Remove"}
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={renameThisDevice} data-testid="settings-rename-device">
            Rename this device
          </Button>
        </CardContent>
      </Card>

      {/* Registration attestation (Option D) */}
      <Card className="border-slate-200" data-testid="hpcsa-attestation-card">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#111827]" />
            <h3 className="font-heading font-semibold text-[#111827]">Registration attestation</h3>
          </div>
          <div className="text-sm text-slate-600">
            Last confirmed: <strong>{user.last_hpcsa_attestation_at ? fmt(user.last_hpcsa_attestation_at) : "—"}</strong>
            {user.hpcsa_attestation_due && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Re-confirmation due</span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Every 90 days verified clinicians are asked to confirm they still hold their registered HPCSA / MP / student number.
          </p>
          <Button onClick={reAttest} disabled={attesting} variant="outline" size="sm" data-testid="settings-reattest-btn">
            {attesting ? "Confirming..." : "Confirm my registration is current"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-rose-200 bg-rose-50/30" data-testid="danger-zone-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <h3 className="font-heading font-semibold text-rose-900">Danger zone</h3>
          </div>
          <p className="text-sm text-slate-600">
            Permanently delete your RapidCare SA account and all associated data — your profile, favorites,
            viewing history, devices and subscription records. This action is <strong>irreversible</strong> and
            cannot be undone. {isPaid && "Any remaining premium access will be lost without a refund."}
          </p>
          <Dialog open={delOpen} onOpenChange={(o) => { setDelOpen(o); if (!o) { setDelPassword(""); setDelConfirm(""); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="text-rose-700 border-rose-300 hover:bg-rose-100" data-testid="open-delete-account">
                <Trash2 className="w-4 h-4 mr-2" /> Delete my account
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="delete-account-dialog">
              <DialogHeader>
                <DialogTitle className="text-rose-900">Delete your account permanently?</DialogTitle>
                <DialogDescription>
                  This will erase your account and all personal data immediately. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {!isGoogleUser && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">Current password</label>
                    <Input
                      type="password"
                      value={delPassword}
                      onChange={(e) => setDelPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      data-testid="delete-account-password"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Type <span className="font-mono font-bold text-rose-700">DELETE</span> to confirm</label>
                  <Input
                    value={delConfirm}
                    onChange={(e) => setDelConfirm(e.target.value)}
                    placeholder="DELETE"
                    autoComplete="off"
                    data-testid="delete-account-confirm"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDelOpen(false)} disabled={deleting} data-testid="delete-account-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={deleteAccount}
                  disabled={deleting || delConfirm.trim() !== "DELETE" || (!isGoogleUser && !delPassword)}
                  data-testid="delete-account-confirm-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Permanently delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-6 text-xs text-slate-600 leading-relaxed">
          <strong className="text-slate-800">Privacy:</strong> RapidCare SA stores account info, favorites, and viewing history for your use only. We do not accept or store patient-identifiable information. Do not enter patient names, ID numbers, or file numbers anywhere in the platform.
        </CardContent>
      </Card>
    </div>
  );
}
